import React from 'react';
import Icon from '../../../components/AppIcon';

const HowItWorksSection = () => {
  const steps = [
    {
      id: 1,
      icon: "UserPlus",
      title: "Create Anonymous Profile",
      description: "Job seekers register for free and build detailed profiles without revealing personal identity. Focus on skills, experience, and qualifications.",
      color: "primary"
    },
    {
      id: 2,
      icon: "Search",
      title: "Employers Search & Match",
      description: "Companies browse anonymous candidates using advanced filters. See skills and compatibility without bias from names or photos.",
      color: "accent"
    },
    {
      id: 3,
      icon: "CreditCard",
      title: "Pay to Unlock Contact",
      description: "When employers find the right match, they pay 149 NOK to reveal full contact details and start the conversation.",
      color: "success"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Icon name="Lightbulb" size={16} className="mr-2" />
            Simple Process
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            How Workfolio Works
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our three-step process eliminates hiring bias while connecting the right talent 
            with the right opportunities. Fair, fast, and efficient.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps?.map((step, index) => (
            <div key={step?.id} className="relative">
              {/* Connection Line */}
              {index < steps?.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-border to-transparent transform translate-x-6 z-0"></div>
              )}
              
              <div className="relative bg-card rounded-2xl p-8 shadow-elevation border border-border hover:shadow-elevation-lg transition-all duration-300 group">
                {/* Step Number */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  {step?.id}
                </div>
                
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-${step?.color}/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={step?.icon} size={32} className={`text-${step?.color}`} />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {step?.title}
                </h3>
                
                <p className="text-muted-foreground leading-relaxed">
                  {step?.description}
                </p>
                
                {/* Hover Effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br from-${step?.color}/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}></div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-card rounded-2xl p-8 shadow-elevation border border-border max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Ready to Experience Fair Hiring?
            </h3>
            <p className="text-muted-foreground mb-6">
              Join thousands of job seekers and employers who trust Workfolio for unbiased recruitment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/role-selection-modal"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="ArrowRight" size={20} className="mr-2" />
                Start Your Journey
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted/50 transition-colors"
              >
                <Icon name="Info" size={20} className="mr-2" />
                Learn More
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;